import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import {
    fetchWhatsAppTemplates,
    sendWhatsAppTemplateMessage,
} from 'flow/actions/tickets-action';
import BaseModal from 'flow/components/ui-components/BaseModal';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import Skeleton from 'flow/components/ui-components/Skeleton';
import useAppContext from 'flow/hooks/useAppContext';
import { TicketKey } from 'flow/types/EntityKeys';
import { getFlagForLanguage } from 'flow/utils/country-utils-TW';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { twMerge } from 'tailwind-merge';
import useTicketAction from 'flow/hooks/useTicketAction';

interface TemplatesPayload {
    data: Template[];
}

interface Template {
    category: 'UTILITY' | string;
    components: TemplateComponent[];
    id: string;
    language: string;
    name: string;
    status: string;
}

interface TemplateComponent {
    format: 'TEXT' | string;
    text: string;
    type: ComponentType;
}

type ComponentType = 'HEADER' | 'BODY' | 'FOOTER';

const getTemplatesForDropdown = (templates?: Template[]) => {
    const sorted = templates?.sort((a, b) =>
        a.language.localeCompare(b.language),
    );
    return (
        sorted?.map((item) => ({
            key: item.id,
            label: `${getFlagForLanguage(item.language)} ${item.name}`,
        })) || []
    );
};

interface Props {
    ticketKey?: TicketKey;
}

const WhatsAppTemplateModal = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();

    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();
    const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
    const [templateSelectedId, setTemplateSelectedId] = useState(undefined);
    const [templates, setTemplates] = useState<Template[]>(undefined);

    useEffect(() => {
        setIsFetchingTemplates(true);
        runTicketAction(fetchWhatsAppTemplates(ticketKey))
            .then((payload: TemplatesPayload) => {
                const utilityTemplates = payload.data.filter(
                    (item) => item.category === 'UTILITY',
                );
                setTemplates(utilityTemplates);
                if (utilityTemplates.length === 1) {
                    setTemplateSelectedId(utilityTemplates[0].id);
                }
                setIsFetchingTemplates(false);
            })
            .catch(() => {
                setIsFetchingTemplates(false);
            });
    }, [dispatch, ticketKey]);

    const templateSelected = useMemo(
        () => templates?.find((entry) => entry.id === templateSelectedId),
        [templates, templateSelectedId],
    );

    const onClickPostReply = () => {
        if (!templateSelectedId || !templateSelected) {
            return;
        }

        runTicketAction(
            sendWhatsAppTemplateMessage(
                ticketKey,
                templateSelected?.name,
                templateSelected?.language,
            ),
        );

        dispatch(
            segmentTrack(TICKET_STRINGS.CONFIRM_WHATSAPP_TEMPLATE, {
                component: 'WhatsApp templates modal',
            }),
        );
    };

    const getComponent = (type: ComponentType) => {
        const component = templateSelected?.components?.find(
            (item) => item.type === type,
        );
        if (!component) return null;
        if (!component.text)
            return (
                <span className="italic">
                    {i18next.t('tickets.whatsapp.template.data_no_preview')}
                </span>
            );

        return <span>{component.text}</span>;
    };

    const getPreviewBody = () => {
        if (isFetchingTemplates) {
            return (
                <>
                    <SkeletonTextLine width="w-20" />
                    <SkeletonTextLine width="w-24" />
                </>
            );
        }

        if (noTemplatesFound) {
            return (
                <span className="text-gray-400 italic">
                    {i18next.t(
                        'tickets.whatsapp.template.none_found_instructions',
                    )}
                </span>
            );
        }

        if (!templateSelected) {
            return (
                <span className="text-gray-400 italic">
                    {i18next.t('tickets.whatsapp.template.select_preview')}
                </span>
            );
        }

        return (
            <>
                {getComponent('HEADER')}
                {getComponent('BODY')}
                {getComponent('FOOTER')}
            </>
        );
    };

    const noTemplatesFound = templates?.length === 0 && !isFetchingTemplates;

    const getDropdownMenuTitle = () => {
        if (templateSelected) {
            return `${getFlagForLanguage(templateSelected.language)} ${templateSelected.name}`;
        }

        if (noTemplatesFound) {
            return i18next.t('tickets.whatsapp.template.none_found');
        }

        return i18next.t('tickets.whatsapp.template.select_send');
    };

    return (
        <BaseModal
            open={true}
            header={
                <span className="text-lg text-gray-800">
                    {i18next.t('tickets.whatsapp.reply_template')}
                </span>
            }
            disableSubmit={!templateSelectedId}
            disableHeaderForcedLowerCase={true}
            onSubmit={onClickPostReply}
            confirmButtonText={i18next.t('tickets.reply.post')}
            className="min-h-[564px] w-[420px]"
        >
            <div className="flex grow flex-col justify-between">
                <div>
                    <div className="mt-3 flex flex-col gap-y-1.5">
                        <div className="text-xs font-bold">
                            {i18next.t('tickets.whatsapp.template')}
                        </div>
                        <div className="flex flex-col gap-y-2">
                            {isFetchingTemplates ? (
                                <>
                                    <SkeletonBar />
                                    <SkeletonBar />
                                </>
                            ) : (
                                <DropdownMenuComponent
                                    options={getTemplatesForDropdown(templates)}
                                    title={getDropdownMenuTitle()}
                                    onItemSelected={setTemplateSelectedId}
                                    disabled={noTemplatesFound}
                                />
                            )}
                        </div>
                    </div>
                    <div className="mt-4 flex flex-col gap-y-1.5">
                        <div className="text-xs font-bold">
                            {i18next.t('preview')}
                        </div>
                        <div className="h-[184px]">
                            <div className="scrollbar max-h-[184px] overflow-y-scroll rounded-sm bg-gray-100">
                                <div className="flex flex-col gap-y-3 p-2">
                                    {getPreviewBody()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-6 text-sm text-gray-500">
                    {i18next.t('tickets.whatsapp.locked_template_info')}
                </div>
            </div>
        </BaseModal>
    );
};

export default React.memo(WhatsAppTemplateModal);

const SkeletonBar = () => {
    return (
        <div className="animate-fade flex h-10 w-full items-center rounded-sm bg-gray-200 text-gray-300">
            <div className="px-3">
                <Skeleton.Text className="h-4 w-28" />
            </div>
        </div>
    );
};

interface SkeletonLineProps {
    width: string;
}

const SkeletonTextLine = ({ width }: SkeletonLineProps) => {
    return (
        <div className="flex h-6 items-center">
            <Skeleton.Text
                className={twMerge(
                    'animate-fade h-5 px-3 text-gray-300',
                    width,
                )}
            />
        </div>
    );
};
